import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Repères de l'Institute of Medicine (IOM, 2009) pour la prise de poids pendant
// une grossesse simple (mono-fœtale), selon l'IMC d'avant grossesse.
const CATEGORIES = [
  { key: 'insuffisant', labelKey: 'gwg_cat_under', range: [18.5, -Infinity], total: [12.5, 18], weekly: [0.44, 0.58] },
  { key: 'normal', labelKey: 'gwg_cat_normal', range: [24.9, 18.5], total: [11.5, 16], weekly: [0.35, 0.5] },
  { key: 'surpoids', labelKey: 'gwg_cat_over', range: [29.9, 25], total: [7, 11.5], weekly: [0.23, 0.33] },
  { key: 'obesite', labelKey: 'gwg_cat_obese', range: [Infinity, 30], total: [5, 9], weekly: [0.17, 0.27] },
];

const findCategory = (bmi) => {
  if (bmi < 18.5) return CATEGORIES[0];
  if (bmi < 25) return CATEGORIES[1];
  if (bmi < 30) return CATEGORIES[2];
  return CATEGORIES[3];
};

const GestationalWeightGain = () => {
  const { t } = useLanguage();
  const [heightCm, setHeightCm] = useState('');
  const [prePregWeight, setPrePregWeight] = useState('');
  const [currentWeight, setCurrentWeight] = useState('');
  const [week, setWeek] = useState('');

  const h = parseFloat(heightCm) / 100;
  const w0 = parseFloat(prePregWeight);
  const wNow = parseFloat(currentWeight);
  const wk = parseFloat(week);

  const bmi = h > 0 && w0 > 0 ? w0 / (h * h) : null;
  const cat = bmi ? findCategory(bmi) : null;

  let expectedRange = null;
  let gainSoFar = null;
  if (cat && wk >= 0 && wk <= 42) {
    // Repère : gain quasi nul les 13 premières semaines (~0.5 à 2 kg au total sur
    // tout le 1er trimestre, similaire pour toutes les catégories), puis rythme
    // hebdomadaire propre à la catégorie à partir de la semaine 13.
    const weeksInto2nd3rd = Math.max(0, wk - 13);
    const lowFirstTri = 0.5;
    const highFirstTri = 2;
    const low = (wk < 13 ? (lowFirstTri * wk) / 13 : lowFirstTri + cat.weekly[0] * weeksInto2nd3rd);
    const high = (wk < 13 ? (highFirstTri * wk) / 13 : highFirstTri + cat.weekly[1] * weeksInto2nd3rd);
    expectedRange = [Math.max(0, low), high];
  }
  if (wNow > 0 && w0 > 0) {
    gainSoFar = wNow - w0;
  }

  return (
    <ToolPageLayout
      title={t('gwg_title')}
      subtitle={t('gwg_subtitle')}
      specialty="gynecologue"
    >
      <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-8">
        <Info className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          {t('gwg_info')}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('gwg_height_label')}</Label>
          <Input type="number" min="120" max="220" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} placeholder="Ex: 165" className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('gwg_preweight_label')}</Label>
          <Input type="number" min="30" max="200" value={prePregWeight} onChange={(e) => setPrePregWeight(e.target.value)} placeholder="Ex: 62" className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('gwg_currentweight_label')}</Label>
          <Input type="number" min="30" max="250" value={currentWeight} onChange={(e) => setCurrentWeight(e.target.value)} placeholder="Ex: 68" className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('gwg_week_label')}</Label>
          <Input type="number" min="0" max="42" value={week} onChange={(e) => setWeek(e.target.value)} placeholder="Ex: 24" className="rounded-xl" />
        </div>
      </div>

      {cat && (
        <div className="space-y-4">
          <div className="rounded-2xl p-6 text-center border border-[#0596DE]/20 bg-[#E6F4FB]">
            <div className="text-sm text-[#64748B] mb-1">{t('gwg_your_bmi_prefix')} {bmi.toFixed(1)}</div>
            <div className="text-xl font-bold text-[#0596DE]">{t(cat.labelKey)}</div>
            <div className="mt-3 text-sm text-[#0F172A]">
              {t('gwg_total_recommended')}{' '}
              <span className="font-semibold">{cat.total[0]}–{cat.total[1]} kg</span>
            </div>
          </div>

          {expectedRange && (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
              <p className="text-sm text-[#64748B] mb-1">{t('gwg_indicative_at_week')} {wk} {t('gwg_weeks_word')}</p>
              <p className="text-lg font-semibold text-[#0F172A]">
                {t('gwg_about_prefix')} {expectedRange[0].toFixed(1)}–{expectedRange[1].toFixed(1)} {t('gwg_kg_gained_since_start')}
              </p>
              {gainSoFar !== null && (
                <p className={`mt-2 text-sm ${gainSoFar < expectedRange[0] - 1 || gainSoFar > expectedRange[1] + 1 ? 'text-[#F59E0B] font-medium' : 'text-[#64748B]'}`}>
                  {t('gwg_current_gain_prefix')} {gainSoFar >= 0 ? '+' : ''}{gainSoFar.toFixed(1)} kg
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('gwg_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default GestationalWeightGain;
